// contexts/AuthContext.tsx
import {
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  updateProfile,
  type Persistence,
  type UserCredential,
} from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Beacon, BeaconEventType, ConstantsManager, Device, PushNotificationManager, User } from "../Entities";
import restGate from "../RestGate";
import Loading from "../components/common/Loading/Loading";
import { app } from "../firebase";
import { useLocation, type NavigateFunction } from "react-router-dom";

/**
 * loading       – Firebase hasn't resolved yet; show a spinner
 * anonymous     – Firebase resolved, no logged-in user
 * authenticated – Firebase resolved, user is logged in and session is ready
 */
export type AuthStatus = "loading" | "anonymous" | "authenticated";

type AuthMethod = "email" | "google";

interface RegisterParams {
  method: AuthMethod;
  firstName?: string;
  lastName?: string;
  email?: string;
  name?: string;
  password?: string;
  birthTimestamp?: number;
  rememberMe?: boolean;
  referralCode?: string;
}

interface LoginParams {
  method: AuthMethod;
  email?: string;
  password?: string;
  rememberMe?: boolean;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  authStatus: AuthStatus;
  authError: string | null;
  regError: string | null;
  setAuthError: React.Dispatch<React.SetStateAction<string | null>>;
  setRegError: React.Dispatch<React.SetStateAction<string | null>>;
  onLogin: (params: LoginParams) => Promise<void>;
  onLogout: () => Promise<void>;
  onRegister: (params: RegisterParams) => Promise<User>;
  onPasswordChange: (params: { currentPassword: string; newPassword: string }) => Promise<void>;
  onEmailChange: (newEmail: string) => Promise<void>;
  isLoggedByEmail: () => boolean;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
}

// ─────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────

export const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

// ─────────────────────────────────────────────────────────────
// Error translation
// ─────────────────────────────────────────────────────────────

const FIREBASE_ERROR_MAP: Record<string, string> = {
  "Firebase: Error (auth/email-already-in-use).": "Tento email již existuje",
  "Firebase: Error (auth/invalid-credential).": "Špatné přihlašovací údaje",
};

const translateError = (message: string): string => FIREBASE_ERROR_MAP[message] ?? message;

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────

export const AuthProvider = ({ children, navigate }: { children: ReactNode; navigate: NavigateFunction }) => {
  const [authStatus, setAuthStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [regError, setRegError] = useState<string | null>(null);
  const location = useLocation();

  /**
   * Prevents onAuthStateChanged from double-handling a sign-in that was already
   * handled by onLogin / onRegister directly. A ref (not state) so it never
   * triggers re-renders.
   */
  const handledByFlowRef = useRef(false);

  /** True once Firebase + device + user sessions are all ready. */
  const loading = authStatus === "loading";

  // ── Helpers ──────────────────────────────────────────────

  const firebaseSignOut = async (): Promise<void> => {
    try {
      handledByFlowRef.current = true;
      await signOut(getAuth(app));
    } catch (err) {
      console.error("Firebase sign-out error:", err);
    }
  };

  const isLoggedByEmail = (): boolean => user?.provider_id === "password";

  // ── Session layer 1 – Device (always exists) ─────────────

  const initDeviceSession = async (resolvedUser: User | null = null): Promise<void> => {
    await Device.ensureCreated();
    if (resolvedUser) {
      await Device.pairUser();
    }
  };

  // ── Session layer 2 – User (only when authenticated) ─────

  const startUserSession = async (resolvedUser: User): Promise<void> => {
    await ConstantsManager.onSessionStart(resolvedUser);
    await PushNotificationManager.onSessionStart();
    setUser(resolvedUser);
    setAuthStatus("authenticated");
  };

  const endUserSession = async (): Promise<void> => {
    setUser(null);
    await ConstantsManager.onSessionEnd();
    await PushNotificationManager.onSessionEnd();
    setAuthStatus("anonymous");
  };

  // ── Beacon helpers ────────────────────────────────────────

  const initBeacon = (): void => {
    Beacon.init();
    Beacon.addEvent(BeaconEventType.ON_PAGE_OPEN, { url: window.location.pathname });
  };

  useEffect(() => {
    Beacon.addEvent(BeaconEventType.ON_PAGE_CHANGE, { url: location.pathname });
  }, [location.pathname]);

  // ── Firebase result → app user ───────────────────────────

  const setTentativeFirebaseUser = (firebaseResult: UserCredential): void => {
    restGate.tentativeFirebaseUser = firebaseResult.user;
  };

  const clearTentativeFirebaseUser = (): void => {
    restGate.tentativeFirebaseUser = null;
  };

  // ── Auth flow helpers ─────────────────────────────────────

  const getPersistence = (rememberMe?: boolean): Persistence =>
    rememberMe ? browserLocalPersistence : browserSessionPersistence;

  const signInWithGoogle = async (): Promise<UserCredential> => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(getAuth(app), provider);
  };

  // ─────────────────────────────────────────────────────────
  // PUBLIC AUTH ACTIONS
  // ─────────────────────────────────────────────────────────

  const onRegister = useCallback(
    async ({ method, firstName, lastName, email, name, password, birthTimestamp, rememberMe, referralCode }: RegisterParams): Promise<User> => {
      try {
        handledByFlowRef.current = true;
        await setPersistence(getAuth(app), getPersistence(rememberMe));

        let firebaseResult: UserCredential;
        if (method === "email") {
          firebaseResult = await createUserWithEmailAndPassword(getAuth(app), email as string, password as string);
          await updateProfile(firebaseResult.user, { displayName: name });
        } else if (method === "google") {
          firebaseResult = await signInWithGoogle();
        } else {
          throw new Error("Unknown registration method");
        }

        setTentativeFirebaseUser(firebaseResult);

        await User.create({ referralCode, name, firstName, lastName, birthTimestamp });
        const resolvedUser = await User.get();

        await initDeviceSession(resolvedUser);
        await startUserSession(resolvedUser);
        Beacon.addEvent(BeaconEventType.ON_REGISTER);

        return resolvedUser;
      } catch (err) {
        console.error("Registration error:", err);
        handledByFlowRef.current = false;
        setRegError(translateError((err as Error).message));
        throw err;
      }
    },
    [],
  );

  const onLogin = useCallback(
    async ({ method, email, password, rememberMe }: LoginParams): Promise<void> => {
      try {
        handledByFlowRef.current = true;
        await setPersistence(getAuth(app), getPersistence(rememberMe));

        let firebaseResult: UserCredential;
        if (method === "email") {
          firebaseResult = await signInWithEmailAndPassword(getAuth(app), email as string, password as string);
        } else if (method === "google") {
          firebaseResult = await signInWithGoogle();
        } else {
          throw new Error("Unknown login method");
        }

        setTentativeFirebaseUser(firebaseResult);

        let resolvedUser: User;
        try {
          resolvedUser = await User.get();
        } catch (err) {
          if ((err as Error).message === "user not registered") {
            await User.deleteFirebaseUser();
          }
          throw err;
        }

        await initDeviceSession(resolvedUser);
        await startUserSession(resolvedUser);
        Beacon.addEvent(BeaconEventType.ON_LOGIN);

        navigate("/dashboard");
      } catch (err) {
        console.error("Login error:", err);
        handledByFlowRef.current = false;
        setAuthError(translateError((err as Error).message));
        throw err;
      }
    },
    [navigate],
  );

  const onLogout = useCallback(async (): Promise<void> => {
    await firebaseSignOut();
    await endUserSession();
    Beacon.addEvent(BeaconEventType.ON_LOGOUT);
    navigate("/login");
    window.scrollTo({ top: 0, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ─────────────────────────────────────────────────────────
  // ACCOUNT MANAGEMENT
  // ─────────────────────────────────────────────────────────

  const onPasswordChange = async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }): Promise<void> => {
    const currentUser = getAuth(app).currentUser;
    if (!currentUser?.email) throw new Error("No authenticated user");
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
    await updatePassword(currentUser, newPassword);
  };

  const onEmailChange = async (newEmail: string): Promise<void> => {
    if (!isLoggedByEmail()) {
      throw new Error("Email change is only available for email/password accounts.");
    }
    const updatedUser = new User(user as User);
    updatedUser.email = newEmail;
    await updatedUser.update();
  };

  // ─────────────────────────────────────────────────────────
  // FIREBASE AUTH STATE LISTENER (handles page-refresh case only)
  // ─────────────────────────────────────────────────────────

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(app), async (firebaseUser) => {
      // onLogin / onRegister / onLogout already handled this auth change — skip.
      if (handledByFlowRef.current) {
        handledByFlowRef.current = false;
        return;
      }

      try {
        if (!firebaseUser) {
          await initDeviceSession(null);
          initBeacon();
          setAuthStatus("anonymous");
          return;
        }

        clearTentativeFirebaseUser();
        const resolvedUser = await User.get();

        await initDeviceSession(resolvedUser);
        await startUserSession(resolvedUser);
        initBeacon();
      } catch (err) {
        console.error("Auth state restore error:", err);
        setAuthError(translateError((err as Error).message));
        await firebaseSignOut();
        setAuthStatus("anonymous");
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────
  // PAGE CLOSE – flush beacon events
  // ─────────────────────────────────────────────────────────

  useEffect(() => {
    const handlePageClose = (): void => {
      Beacon.addEvent(BeaconEventType.ON_PAGE_CLOSE, { url: location.pathname });
      Beacon.flushEvents();
    };

    window.addEventListener("beforeunload", handlePageClose);
    return () => window.removeEventListener("beforeunload", handlePageClose);
  }, [location.pathname]);

  // ─────────────────────────────────────────────────────────
  // ERROR AUTO-CLEAR (after ~5 s)
  // ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!authError) return;
    const timer = setTimeout(() => setAuthError(null), 4777);
    return () => clearTimeout(timer);
  }, [authError]);

  useEffect(() => {
    if (!regError) return;
    const timer = setTimeout(() => setRegError(null), 4777);
    return () => clearTimeout(timer);
  }, [regError]);

  // ─────────────────────────────────────────────────────────
  // CONTEXT VALUE
  // ─────────────────────────────────────────────────────────

  const value: AuthContextValue = {
    user,
    loading,
    authStatus,
    authError,
    regError,
    setAuthError,
    setRegError,
    onLogin,
    onLogout,
    onRegister,
    onPasswordChange,
    onEmailChange,
    isLoggedByEmail,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <Loading variant="logo" /> : children}
    </AuthContext.Provider>
  );
};
